/**
 * Quantity-mode (MDOT-24700): keeps the structural cash row fixed at the bottom of the
 * editor and review grids — directly above the pinned grand-total row — via AG Grid's manual row
 * pinning (`enableRowPinning`). Manual pinning renders a pinned COPY of the row while the
 * source row stays in the row model, which is exactly what keeps the grand-total
 * aggregations (Σ weight, Σ market value) correct: pinned rows are excluded from
 * aggregation, model rows are not. The in-viewport source row is hidden through
 * AG Grid's `.ag-row-pinned-source` class (see `third-party-integrations/ag-grid/ag-grid.css`)
 * so cash only ever shows once, in the pinned copy.
 */
import type { RowNode, RowSelectionOptions } from "ag-grid-community";
import type { AgGridReactProps } from "ag-grid-react";
import type { Instrument } from "../const";

export function pinnedCashRowGridProps<T extends Pick<Instrument, "isCash">>() {
	return {
		enableRowPinning: "bottom",
		// Guards the AUTOMATIC unpin path, not the user one (each grid keeps the pin/unpin
		// context-menu entries out of its `getContextMenuItems`): AG Grid unpins any row
		// whose data changes while `isRowPinnable` returns false, and the cash row's data changes
		// on every quantity/market-value edit (rebalance) — so cash must stay pinnable.
		isRowPinnable: (node) => Boolean(node.data?.isCash),
		isRowPinned: (node) => (node.data?.isCash ? "bottom" : null),
		// The hidden source row (display: none, see ag-grid.css) must also not RESERVE space,
		// or the grid keeps a row-sized hole where cash would have sorted. AG Grid clamps
		// callback heights to >= 1px, so 1 is the effective minimum. Two guards:
		// - `rowPinned`: the same callback sizes the pinned COPY, which must keep the default height;
		// - `pinnedSibling`: the pinned copy is created by COPYING the source node — `rowHeight`
		//   included — and a copied height is never recomputed. Collapsing the source before its
		//   copy exists would therefore bake the 1px into the pinned row. `pinnedSibling` is only
		//   set once the copy exists, and `onPinnedRowsChanged` below re-runs this callback then.
		getRowHeight: (params) =>
			params.node.data?.isCash && !params.node.rowPinned && (params.node as RowNode<T>).pinnedSibling ? 1 : undefined,
		onPinnedRowsChanged: (event) => {
			event.api.resetRowHeights();
		},
	} satisfies Partial<AgGridReactProps<T>>;
}

/**
 * Cash is a structural row (its value is derived, never user-managed): with the source row
 * hidden, a header select-all would otherwise tick it invisibly and expose it to bulk
 * delete. Spread into the editor's `checkboxColumn` params in quantity mode — this both
 * excludes cash from select-all and removes the checkbox from the pinned copy.
 */
export const cashRowSelectionGuard = {
	isRowSelectable: (node) => !node.data?.isCash,
	// Without this the pinned cash copy renders a greyed-out (disabled) checkbox.
	hideDisabledCheckboxes: true,
} satisfies Partial<RowSelectionOptions<Instrument>>;

/**
 * The pinned copy displays the data object of its SOURCE row node; if the editor's search
 * box filters the cash source out of `rowData`, that link breaks and the pinned copy
 * freezes on stale values (edits made while searching keep rebalancing cash). Quantity
 * mode therefore always keeps the cash row in the grid's rows, search or not.
 */
export function keepCashRow(filtered: Instrument[], all: Instrument[]): Instrument[] {
	if (filtered.some((instrument) => Boolean(instrument.isCash))) {
		return filtered;
	}
	const cashRow = all.find((instrument) => Boolean(instrument.isCash));
	if (!cashRow) {
		return filtered;
	}
	return [...filtered, cashRow];
}

/** The structural cash row is not an asset: a composition holding only cash is empty. */
export function holdsAssets(composition: Instrument[]): boolean {
	return composition.some((instrument) => !instrument.isCash);
}
